/* global Blockly */
/* global $ */
/* global Util */
import React from 'react';
import _ from 'underscore';
import { plusImage, minusImage, IMAGE_WIDTH, IMAGE_HEIGHT, getDropdownFields, addDropdownKey, removeDropdownKey, updateDropdownsAfterChange, 
    openSetSchemaWindow, generateOutputPathCode } from '../../helpers/jsonWithSchema';
import { _defineHelpUrlandToolTip, markBlockAsDeprecated } from '../../helpers/index.js'

function defineBlock() {
    return {
        init: function () {
            markBlockAsDeprecated(this);
            this.jsonStructure = this.jsonStructure || {};
            this.appendValueInput('JSON_OBJECT')
                .appendField('from JSON object')
                .setCheck(['String', 'Object', 'Array']);
            this.dropdownInput = this.appendDummyInput('DROPDOWN_PATH')
                                     .appendField('get', 'LABEL')
                                     .appendField(new Blockly.FieldImage(plusImage, IMAGE_WIDTH, IMAGE_HEIGHT, '*', () => this.addKey()), 'PLUS')
                                     .appendField(new Blockly.FieldImage(minusImage, IMAGE_WIDTH, IMAGE_HEIGHT, '*', () => this.deleteKey()), 'DELETE');
            this.setColour(Blockly.Msg.LISTS_HUE);
            this.setOutput(true);
            this.setOutputShape(Blockly.OUTPUT_SHAPE_ROUND);
            this.setTooltip('Get a value nested within a JSON object. If the input is a string, the string will be parsed into a JSON object.');
            this.setHelpUrl(Blockly.BASE_HELP_URL + "#lists");
            // initialize Set Schema button
            this.appendDummyInput('SCHEMA')
                .appendField(new Blockly.FieldButton('Set Schema', this.setSchema, () => this.setSchemaButtonHandler()), 'SCHEMA');
        },

        mutationToDom: function () {
            // get the selected path from the dropdown fields
            const selectedPath = getDropdownFields(this.dropdownInput).map(field => field.getValue());

            // mutation keys are strictly lowercase in Blockly
            const mutation = Util.dom('mutation', {
                selectedpath: selectedPath,
                jsonstructure: JSON.stringify(this.jsonStructure)
            });

            return mutation;
        },

        domToMutation: function (xml) {
            // get the selected path for the dropdown fields as an array
            let selectedPath = xml.getAttribute('selectedpath');
            selectedPath = selectedPath ? selectedPath.split(',') : [];

            // set the JSON structure (schema) for the block
            let jsonStr = xml.getAttribute('jsonstructure');
            if (jsonStr) {
                try {
                    this.jsonStructure = JSON.parse(jsonStr);
                } catch (e) {
                    console.error('Failed to parse JSON structure for block:', e);
                    this.jsonStructure = {};
                }
            } else {
                this.jsonStructure = {};
            }

            if (Object.keys(this.jsonStructure).length && selectedPath.length) {
                this.buildDropdownTree(selectedPath);
            }
        },

        buildDropdownTree: function (selectedPath) {
            for (let i = 0; i < selectedPath.length; i++) {
                this.addKey(selectedPath[i]);
            }
        },

        addKey: function (selectedValue = null) {
            addDropdownKey({
                input: this.dropdownInput,
                jsonStructure: this.jsonStructure,
                selectedValue: selectedValue,
                updateCallback: this.update.bind(this),
                plusClick: () => this.addKey(),
                minusClick: () => this.deleteKey()
            });
        },

        deleteKey: function () {
            removeDropdownKey({
                input: this.dropdownInput,
                jsonStructure: this.jsonStructure
            });
        },

        update: function (level) {
            updateDropdownsAfterChange({
                input: this.dropdownInput,
                jsonStructure: this.jsonStructure,
                changedDropdownIndex: level
            });
        },

        setSchemaButtonHandler: function () {
            openSetSchemaWindow({
                blockRef: this,
                includeQueryButton: false,
                includeCopyButton: false,
                inputSizeLimit: 1 * 1024 * 1024, // 1 MB limit
                applyHandler: (responseData) => {
                    this.jsonStructure = responseData;
                    this.update(-1); // remove all existing dropdowns since the JSON structure has now changed
                }
            });
        }
    };
}

function defineGenerators() {
    return {
        'JavaScript': function (block) {
            const dropdownFields = getDropdownFields(block.dropdownInput);
            let jsonObject = Blockly.JavaScript.valueToCode(block, 'JSON_OBJECT', Blockly.JavaScript.ORDER_NONE);

            // parse input from string to JSON if input is a string
            // if input is a string that is not valid JSON, return an empty string
            jsonObject = jsonObject ? `
                (function () {
                    if (typeof ${jsonObject} !== 'string') return ${jsonObject};
                    try { return JSON.parse(${jsonObject}); } catch (e) { return ''; }
                })()` : '';
            
            return generateOutputPathCode({
                baseExpression: `(${jsonObject})`,
                dropdownFields: dropdownFields,
                includeStructureComment: false
            });
        }
    };
}

const GetFromJSONWithSchema = function (props) {
    let name = 'get_from_json_with_schema';
    GetFromJSONWithSchema.blockName = name;

    Blockly.Blocks[name] = defineBlock(props.context);
    let generators = defineGenerators(props.context);
    for (let i in generators) {
        Blockly[i][name] = generators[i];
    }

    _defineHelpUrlandToolTip(name,  Blockly.BASE_HELP_URL + "#lists");
    
    return <block type={name}></block>;
}

export default GetFromJSONWithSchema;
